import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import { type PromotionPlacement } from '@onr/schema'
import { Link } from '@tanstack/react-router'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'

import { type PromotionOption } from '@/components/model/PromotionOption'
import { AddToPlanDialog } from '@/components/organisms/AddToPlanDialog'
import { AdminHeader, type AdminHeaderProps } from '@/components/organisms/AdminHeader/AdminHeader'
import { PromotionOptionCard } from '@/components/organisms/PromotionOptionCard'
import { PromotionPlanSummary } from '@/components/organisms/PromotionPlanSummary'
import { RegionPromotionTabs } from '@/components/organisms/RegionPromotionTabs'
import { RemoveFromPlanDialog, type RemoveFromPlanDialogProps } from '@/components/organisms/RemoveFromPlanDialog'
import { cn } from '@/lib/utils'

export type PromotionPlanChangeResult = 'done' | 'locked' | 'failed'

export interface PromotionShopViewProps
  extends Pick<AdminHeaderProps, 'regionName' | 'onBackToPiloteClicked'>, React.HTMLAttributes<HTMLDivElement> {
  readonly initiativeId: string
  readonly regionId: string
  readonly year?: number
  readonly options: ReadonlyArray<PromotionOption>
  readonly promotionsUsing: (placement: PromotionPlacement) => RemoveFromPlanDialogProps['promotions']
  readonly onAdd: (placement: PromotionPlacement) => Promise<PromotionPlanChangeResult>
  readonly onRemove: (placement: PromotionPlacement) => Promise<PromotionPlanChangeResult>
}

interface PendingChange {
  readonly kind: 'add' | 'remove'
  readonly placement: PromotionPlacement
}

export const PromotionShopView = React.forwardRef<HTMLDivElement, PromotionShopViewProps>(
  (
    {
      initiativeId,
      regionId,
      regionName,
      year,
      options,
      promotionsUsing,
      onAdd,
      onRemove,
      onBackToPiloteClicked,
      className,
      ...props
    },
    ref
  ) => {
    const intl = useIntl()
    const [change, setChange] = useState<PendingChange>()
    const [pending, setPending] = useState(false)
    const [error, setError] = useState<string>()

    const open = useCallback((kind: PendingChange['kind'], placement: PromotionPlacement) => {
      setError(undefined)
      setChange({ kind, placement })
    }, [])

    const close = useCallback(
      (isOpen: boolean) => {
        if (!isOpen && !pending) {
          setChange(undefined)
          setError(undefined)
        }
      },
      [pending]
    )

    const confirm = useCallback(async () => {
      if (!change) return
      setPending(true)
      setError(undefined)
      const result = await (change.kind === 'add' ? onAdd : onRemove)(change.placement)
      setPending(false)
      switch (result) {
        case 'done':
          setChange(undefined)
          break
        case 'locked':
          setError(intl.formatMessage({ id: 'promotions.shop.removeDialog.locked' }))
          break
        case 'failed':
          setError(intl.formatMessage({ id: 'promotions.shop.dialog.error' }))
          break
      }
    }, [change, intl, onAdd, onRemove])

    const selected = change && options.find((option) => option.placement === change.placement)
    const removalLocked = change?.kind === 'remove' && selected?.state === 'locked'

    return (
      <div className={cn('flex h-full flex-col', className)} ref={ref} {...props}>
        <AdminHeader regionName={regionName} onBackToPiloteClicked={onBackToPiloteClicked} />

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 pb-12 pl-16">
          <h1 className="text-xl text-gray-900 select-none">{intl.formatMessage({ id: 'promotions.region.title' })}</h1>
          <RegionPromotionTabs initiativeId={initiativeId} regionId={regionId} activeTab="shop" />

          <div className="flex flex-wrap items-start gap-6">
            <div className="grid min-w-0 flex-[999_1_700px] grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
              {options.map((option) => (
                <PromotionOptionCard
                  key={option.placement}
                  option={option}
                  onAdd={() => {
                    open('add', option.placement)
                  }}
                  onRemove={() => {
                    open('remove', option.placement)
                  }}
                />
              ))}
            </div>
            <PromotionPlanSummary
              className="sticky top-4 max-w-[340px] flex-[1_1_280px]"
              year={year}
              options={options}
              createPromotionLink={
                <Link
                  to="/promotions/$initiativeId/$regionId"
                  params={{ initiativeId, regionId }}
                  className="text-base font-bold text-blue-700 hover:text-blue-800">
                  {intl.formatMessage({ id: 'promotions.shop.plan.createPromotion' })}
                </Link>
              }
            />
          </div>
        </div>

        <AddToPlanDialog
          open={change?.kind === 'add'}
          onOpenChange={close}
          option={selected}
          pending={pending}
          error={error}
          onConfirm={() => {
            handleNotAwaitedPromise(confirm())
          }}
        />
        <RemoveFromPlanDialog
          open={change?.kind === 'remove'}
          onOpenChange={close}
          option={selected}
          promotions={change?.kind === 'remove' ? promotionsUsing(change.placement) : []}
          locked={removalLocked}
          pending={pending}
          error={removalLocked ? intl.formatMessage({ id: 'promotions.shop.removeDialog.locked' }) : error}
          onConfirm={() => {
            handleNotAwaitedPromise(confirm())
          }}
        />
      </div>
    )
  }
)
